import { apiKey, db, retmax, retmode } from "../constants/env";
import { esearch, esummary } from "../constants/urls";
import {
  delayedFetch,
  abbreviatePageNumbers,
  customEndsWith,
  capitalizeAfterColon,
} from "./styling";
import { checkQuality } from "../components/utils/util";
import { searchInGenAI } from "./fetchFromGenAI";
import journalService from "./journalService";

export const removeDOIUrls = (references) => {
  return references.map((ref) => ({
    ...ref,
    genTerm: ref.term
      .replace(/\s*https?:\/\/(?:dx\.)?doi\.org\/\S+$/i, "")
      .trim(),
  }));
};

export const searchInPubMed = async (references, setProgressStep, mode = 'default', citationStyle = 'vancouver') => {
  const pubMedArray = [];
  const notFoundArray = [];
  const multipleFound = [];
  const withoutDOIRef = removeDOIUrls(references);
  let extractingCurrent = 0;
  if (setProgressStep) {
    setProgressStep({
      step: 'extracting',
      description: `Extracting 0/${withoutDOIRef.length}`,
      current: 0,
      total: withoutDOIRef.length
    });
  }
  // Pass progress callback to searchInGenAI
  const { genAIArray } = await searchInGenAI(withoutDOIRef, false, (current, total) => {
    extractingCurrent = current;
    if (setProgressStep) {
      setProgressStep({
        step: 'extracting',
        description: `Extracting ${current}/${total}`,
        current,
        total
      });
    }
  });
  if (setProgressStep) {
    setProgressStep({
      step: 'pubmed',
      description: `Searching PubMed 0/${genAIArray.length}`,
      current: 0,
      total: genAIArray.length
    });
  }
  let pubmedCurrent = 0;
  const keepIssue = mode === 'ditech';

  for (const ref of genAIArray) {
    await new Promise(resolve => setTimeout(resolve, 300));
    try {
      let searchResult = null;
      let extractedData = null;
      let searchStrategy = "";

      if (ref.searchStr) {
        searchResult = await mockPubMedSearch(ref.searchStr);
        if (searchResult?.result?.uids?.length > 0) {
          extractedData = await selectBestMatch(searchResult, ref, keepIssue, citationStyle);
          if (extractedData) {
            searchStrategy = "structured_data_all_authors";
          }
        }
      }

      if (!extractedData && ref.searchStr2) {
        searchResult = await mockPubMedSearch(ref.searchStr2);
        if (searchResult?.result?.uids?.length > 0) {
          extractedData = await selectBestMatch(searchResult, ref, keepIssue, citationStyle);
          if (extractedData) {
            searchStrategy = "title_journal_first_author";
          }
        }
      }

      if (!extractedData && ref.searchStr3) {
        searchResult = await mockPubMedSearch(ref.searchStr3);
        if (searchResult?.result?.uids?.length > 0) {
          extractedData = await selectBestMatch(searchResult, ref, keepIssue, citationStyle);
          if (extractedData) {
            searchStrategy = "title_journal_only";
          }
        }
      }

      if (!extractedData && ref.searchStr4) {
        console.log("Strategy 4 - Title only:", ref.searchStr4);
        searchResult = await mockPubMedSearch(ref.searchStr4);
        if (searchResult?.result?.uids?.length > 0) {
          extractedData = await selectBestMatch(searchResult, ref, keepIssue, citationStyle);
          if (extractedData) {
            searchStrategy = "title_only";
          }
        }
      }

      if (extractedData) {
        const quality = checkQuality({ ...ref, ...extractedData });
        pubMedArray.push({
          ...ref,
          ...extractedData,
          type: "FOUND",
          found: searchResult,
          searchStrategy,
          score: quality.score
        });
      } else if (searchResult?.result?.uids?.length > 1) {
        multipleFound.push({
          ...ref,
          type: "MULTIPLE_PubMed",
          found: searchResult,
        });
      } else {
        notFoundArray.push(ref);
      }
    } catch (error) {
      // Handle error silently
      notFoundArray.push(ref);
    }
    // After processing each reference:
    pubmedCurrent++;
    if (setProgressStep) {
      setProgressStep({
        step: 'pubmed',
        description: `Searching PubMed ${pubmedCurrent}/${genAIArray.length}`,
        current: pubmedCurrent,
        total: genAIArray.length
      });
    }
  }
  return { pubMedArray, notFoundArray, multipleFound };
};

// Function to select the best match from multiple PubMed results
async function selectBestMatch(searchResult, originalRef, keepIssue = false, citationStyle = 'vancouver') {
  if (!searchResult?.result?.uids || searchResult.result.uids.length === 0) {
    return null;
  }

  const uids = searchResult.result.uids;

  // If only one result, use it
  if (uids.length === 1) {
    return await extractCitationDetails(searchResult, originalRef, keepIssue, citationStyle);
  }

  // Multiple results - find the best match
  let bestMatch = null;
  let bestScore = 0;

  for (const uid of uids) {
    const article = searchResult.result[uid];
    if (!article) continue;

    let score = 0;

    // Check year match (highest priority) - prefer closer years
    const extractedYear = originalRef.extractedData?.year;
    const articleYear = article.sortpubdate?.split("/")[0];

    if (extractedYear && articleYear) {
      const yearDiff = Math.abs(parseInt(extractedYear) - parseInt(articleYear));
      if (yearDiff === 0) {
        score += 50; // Perfect year match
      } else if (yearDiff <= 2) {
        score += 40; // Very close year (within 2 years)
      } else if (yearDiff <= 5) {
        score += 30; // Close year (within 5 years)
      } else if (yearDiff <= 10) {
        score += 20; // Somewhat close (within 10 years)
      } else {
        score += 5; // Very distant year
      }
    }

    // Check title similarity
    const originalTitle = originalRef.extractedData?.article_title?.toLowerCase() || "";
    const articleTitle = article.title?.toLowerCase() || "";

    if (originalTitle && articleTitle) {
      const titleSimilarity = calculateTitleSimilarity(originalTitle, articleTitle);
      score += titleSimilarity * 30; // Up to 30 points for title similarity
      console.log(`Title similarity: ${titleSimilarity.toFixed(2)} (Original: "${originalTitle}" vs Article: "${articleTitle}")`);
    }

    // Check journal similarity
    const originalJournal = originalRef.extractedData?.journal_title?.toLowerCase() || "";
    const articleJournal = article.source?.toLowerCase() || "";

    if (originalJournal && articleJournal) {
      if (articleJournal.includes(originalJournal) || originalJournal.includes(articleJournal)) {
        score += 20; // Journal match
      }
    }

    if (score > bestScore) {
      bestScore = score;
      bestMatch = { uid, article };
    }
  }

  // Return best match if we have reasonable confidence (score > 20) or fallback to first result
  if (bestMatch && bestScore > 20) {
    const modifiedResult = {
      ...searchResult,
      result: {
        ...searchResult.result,
        uids: [bestMatch.uid]
      }
    };
    return await extractCitationDetails(modifiedResult, originalRef, keepIssue, citationStyle);
  }

  // Fallback: if no good match found, use the first result (most recent/relevant)
  const firstUid = uids[0];
  const modifiedResult = {
    ...searchResult,
    result: {
      ...searchResult.result,
      uids: [firstUid]
    }
  };
  return await extractCitationDetails(modifiedResult, originalRef, keepIssue, citationStyle);
}

// Calculate title similarity between two titles
function calculateTitleSimilarity(title1, title2) {
  if (!title1 || !title2) return 0;

  // Normalize titles - handle hyphen variations better
  const normalize = (str) => {
    return str
      .toLowerCase()
      // Normalize hyphens: "type-a" and "type a" should match
      .replace(/[-\s]+/g, ' ')
      .replace(/[^\w\s]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .split(' ')
      .filter(word => word.length > 2);
  };

  const words1 = normalize(title1);
  const words2 = normalize(title2);

  if (words1.length === 0 || words2.length === 0) return 0;

  // Calculate Jaccard similarity
  const set1 = new Set(words1);
  const set2 = new Set(words2);

  const intersection = new Set([...set1].filter(x => set2.has(x)));
  const union = new Set([...set1, ...set2]);

  return intersection.size / union.size;
}

// Mock functions for demonstration purposes
const mockPubMedSearch = async (term) => {
  // Validate input
  if (!term || typeof term !== 'string') {
    return null;
  }

  // Simulate a PubMed search
  const cleanedTerm = term
    .trim()
    .replaceAll("&amp;", "")
    .replaceAll("&", "");

  // Use encodeURIComponent for proper URL encoding instead of manual + replacement
  const encodedTerm = encodeURIComponent(cleanedTerm);
  let searchUrl = `${esearch}?db=${db}&term=${encodedTerm}&retmode=${retmode}&retmax=${retmax}&api_key=${apiKey}`;
  const searchResponse = await delayedFetch(searchUrl);
  if (!searchResponse.ok) throw new Error("Search API failed");
  const searchResult = await searchResponse.json();

  if (searchResult.esearchresult.idlist.length > 0) {
    const pmids = searchResult.esearchresult.idlist.join(",");
    const summaryUrl = `${esummary}?db=${db}&id=${pmids}&retmode=${retmode}&api_key=${apiKey}`;

    const summaryResponse = await delayedFetch(summaryUrl);
    if (!summaryResponse.ok) throw new Error("Summary API failed");

    return await summaryResponse.json();
  }
};

export const createNewArrayWithoutAutors = (array) => {
  const newArray = [];
  for (const element of array) {
    const newTitle = element.term.split(".").slice(1).join(".").trim();
    newArray.push({ ...element, term: newTitle });
  }
  return newArray;
};

function getCleanJournalName(journalTitle) {
  if (!journalTitle) return "";
  // Try to match a journal name pattern (capitalized words, possibly with spaces, ending with a dot, before a year)
  // Example: "... Cancer Res. 2011;..." => "Cancer Res"
  const match = journalTitle.match(/([A-Z][A-Za-z\s&\-]+)\.\s*\d{4}/);
  if (match) return match[1].trim();
  // Fallback: If it contains a year or a semicolon, try to extract the part before the year/semicolon
  if (journalTitle.match(/\d{4}/) || journalTitle.includes(";")) {
    const match2 = journalTitle.match(/([A-Za-z\s\(\)\-]+)(?=\d{4}|;)/);
    return match2 ? match2[1].trim() : journalTitle.trim();
  }
  return journalTitle.trim();
}

// --- Author formatters per citation style ---

function authorVancouver(rawAuthors) {
  const names = rawAuthors.map(a => a.name);
  const str = names.length > 6 ? names.slice(0, 6).join(', ') + ', et al.' : names.join(', ');
  return customEndsWith(str, '.') ? str : str + '.';
}

function authorAPA(rawAuthors) {
  const toAPA = n => {
    const m = n.match(/^(\S+)\s+(.+)$/);
    if (!m) return n;
    return `${m[1]}, ${m[2].trim().split('').map(i => i + '.').join(' ')}`;
  };
  const fmt = rawAuthors.map(a => toAPA(a.name));
  if (fmt.length > 6) return fmt.slice(0, 6).join(', ') + ', et al.';
  if (fmt.length === 1) return fmt[0];
  return fmt.slice(0, -1).join(', ') + ', & ' + fmt[fmt.length - 1];
}

function authorHarvard(rawAuthors) {
  const names = rawAuthors.map(a => a.name);
  if (names.length > 6) return names.slice(0, 6).join(', ') + ' et al.';
  if (names.length === 1) return names[0];
  return names.slice(0, -1).join(', ') + ' & ' + names[names.length - 1];
}

function authorChicago(rawAuthors) {
  const names = rawAuthors.map(a => a.name);
  if (names.length > 6) return names.slice(0, 6).join(', ') + ' et al.';
  if (names.length === 1) return names[0] + '.';
  return names.slice(0, -1).join(', ') + ', and ' + names[names.length - 1] + '.';
}

async function formatCitation(rawAuthors, title, journal, year, volume, issueRaw, pages, style, keepIssue) {
  const p = abbreviatePageNumbers(pages);
  const issueLower = (issueRaw || '').toLowerCase();

  switch (style) {
    case 'apa': {
      const journalFull = await journalService.getFullJournalName(journal) || journal;
      const authors = authorAPA(rawAuthors);
      const t = customEndsWith(title, '.') ? title : title + '.';
      const vol = volume ? `${volume}${issueRaw ? '(' + issueRaw + ')' : ''}` : '';
      return `${authors} (${year}). ${t} ${journalFull}, ${vol}, ${p || ''}.`;
    }
    case 'harvard': {
      const journalFull = await journalService.getFullJournalName(journal) || journal;
      const authors = authorHarvard(rawAuthors);
      const t = title.replace(/\.$/, '');
      const vol = volume ? `${volume}${issueRaw ? '(' + issueRaw + ')' : ''}` : '';
      return `${authors} (${year}) '${t}', ${journalFull}, ${vol}, pp. ${p || ''}.`;
    }
    case 'chicago': {
      const journalFull = await journalService.getFullJournalName(journal) || journal;
      const authors = authorChicago(rawAuthors);
      const t = title.replace(/\.$/, '');
      const iss = issueRaw ? ` (${issueRaw})` : '';
      return `${authors} ${year}. "${t}." ${journalFull} ${volume || ''}${iss}:${p || ''}.`;
    }
    case 'ama': {
      const authors = authorVancouver(rawAuthors);
      const t = customEndsWith(title, '.') ? title : title + '.';
      const volStr = issueRaw ? `${volume}(${issueRaw})` : volume;
      return `${authors} ${t} ${journal}. ${year};${volStr}${p ? ':' + p : ''}.`;
    }
    case 'vancouver':
    default: {
      const authors = authorVancouver(rawAuthors);
      const t = customEndsWith(title, '.') ? title : title + '.';
      const vol = (keepIssue || issueLower.includes('sup')) ? `${volume}(${issueRaw})` : volume;
      return `${authors} ${t} ${journal} ${year}${year ? ';' : ''}${vol}${p ? ':' + p : ''}.`;
    }
  }
}

const extractCitationDetails = async (data, originalRef = null, keepIssue = false, citationStyle = 'vancouver') => {
  if (!data?.result?.uids?.length) return null;
  const uid = data.result.uids[0];
  const article = data.result[uid];
  if (!article) return null;

  const rawAuthors = article.authors || [];
  const title = capitalizeAfterColon(article.title || '');
  const journal = article.source || '';
  const year = article.sortpubdate?.split('/')[0] || '';
  const pages = article?.pages || '';
  const volume = article.volume || '';
  const issueRaw = article.issue || '';

  return {
    finalStr: await formatCitation(rawAuthors, title, journal, year, volume, issueRaw, pages, citationStyle, keepIssue),
  };
};
